import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CONFIG_SECTIONS, ConfigField } from "config/adminConfig";
import adminConfigService, { ConfigItem } from "services/AdminConfig/adminConfigService";
import leaveMailCcService, { normalizeRecipients } from "services/AdminConfig/leaveMailCcService";
import SectionNav from "./components/SectionNav";
import ConfigTable from "./components/ConfigTable";
import LiriAdminBuilds from "./components/LiriAdminBuilds";
import LeavePolicy from "./components/LeavePolicy/LeavePolicy";
import { isLiriAdmin, parseJwtPayload } from "util/moduleAccess";

const AdminConfig: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // 1. Get the token
  const token = localStorage.getItem("accessToken"); // adjust if your key is different
  const payload = parseJwtPayload(token);

  // 2. Get the user's email from localStorage
  let userEmail = null;
  try {
    // Adjust "user" to match whatever key you use in localStorage for the user object
    const userStr = localStorage.getItem("user");
    if (userStr) {
      const userObj = JSON.parse(userStr);
      userEmail = userObj.email;
    }
  } catch (error) {
    console.error("Failed to parse user from local storage", error);
  }

  // 3. Check if the user is a Liri Admin by passing BOTH the payload and the email
  // (We pass undefined for the 'currentRole' argument so it relies on the payload's role)
  const isLiriAdminUser = isLiriAdmin(payload, undefined, userEmail);

  // 4. Filter sections based on permissions
  const visibleSections = useMemo(() => {
    return CONFIG_SECTIONS.filter((section) => {
      if (section.key === "liriadmin") {
        return isLiriAdminUser;
      }
      return true;
    });
  }, [isLiriAdminUser]);

  const paramSection = searchParams.get("section");
  const activeSection = visibleSections.some((s) => s.key === paramSection)
    ? (paramSection as string)
    : visibleSections[0]?.key;

  const setActiveSection = (key: string) => {
    setSearchParams({ section: key });
  };

  const [fieldItems, setFieldItems] = useState<Record<string, ConfigItem[]>>({});
  const [loadingFields, setLoadingFields] = useState<Set<string>>(new Set());

  const section = visibleSections.find((s) => s.key === activeSection) ?? visibleSections[0];

  const fetchField = useCallback(async (field: ConfigField) => {
    setLoadingFields((prev) => new Set(prev).add(field.key));
    try {
      if (field.variant === "recipients") {
        const res = await leaveMailCcService.getRecipients();
        const emails = normalizeRecipients(res.data);
        const items: ConfigItem[] = emails.map((email, id) => ({ id, name: email }));
        setFieldItems((prev) => ({ ...prev, [field.key]: items }));
      } else {
        const res = await adminConfigService.getAll(field.apiEndpoint);
        setFieldItems((prev) => ({ ...prev, [field.key]: res.data }));
      }
    } catch {
      // show as empty
    } finally {
      setLoadingFields((prev) => {
        const next = new Set(prev);
        next.delete(field.key);
        return next;
      });
    }
  }, []);

  useEffect(() => {
    if (!section?.fields) return;
    section.fields.forEach((f) => {
      if (!fieldItems[f.key]) fetchField(f);
    });
  }, [activeSection, section, fetchField]); // eslint-disable-line

  if (!section) return null;

  return (
    <div className="flex h-screen max-w-7xl mx-auto py-2 gap-5">
      <SectionNav
        sections={visibleSections}
        activeKey={activeSection}
        onChange={setActiveSection}
      />

      {activeSection === "liriadmin" ? (
        <LiriAdminBuilds />
      ) : activeSection === "leavePolicy" ? (
        <LeavePolicy />
      ) : (
        <ConfigTable
          section={section}
          fieldItems={fieldItems}
          loadingFields={loadingFields}
        />
      )}
    </div>
  );
};

export default AdminConfig;